import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './RainView.css';

const RainView = ({ poem, onBack }) => {
  const [revealProgress, setRevealProgress] = useState(0);
  const [ripples, setRipples] = useState([]);
  const [splatters, setSplatters] = useState([]);
  const [puddleVisible, setPuddleVisible] = useState(false);
  const [activeDrops, setActiveDrops] = useState([]);
  const [readyToReveal, setReadyToReveal] = useState(false);
  const [userTriggered, setUserTriggered] = useState(false);

  // Audio refs for crossfade
  const audioRef1 = useRef(null);
  const audioRef2 = useRef(null);
  const activeAudioRef = useRef(1);
  const soundIndexRef = useRef(0);
  const dropIdRef = useRef(0);

  // Sound sources for auto-cycling
  const soundSources = useMemo(() => [
    '/sounds/rain-default.mp3',
    '/sounds/rain-leaves.mp3',
    '/sounds/rain-tin-roof.mp3',
    '/sounds/rain-puddles.mp3'
  ], []);

  // Generate water streaks on glass
  const waterStreaks = useMemo(() =>
    Array.from({ length: 8 }, (_, i) => ({
      id: i,
      left: 10 + Math.random() * 80,
      height: 60 + Math.random() * 120,
      top: 20 + Math.random() * 40,
      delay: Math.random() * 5,
      duration: 8 + Math.random() * 4
    })), []);

  // Split poem into lines
  const lines = poem?.content?.split('\n') || [];

  // Spawn raindrops continuously - falling toward viewer
  useEffect(() => {
    const spawnDrop = () => {
      const id = dropIdRef.current++;
      const x = 10 + Math.random() * 80; // percentage
      const y = 10 + Math.random() * 80; // percentage

      setActiveDrops(prev => [...prev.slice(-25), {
        id,
        x,
        y,
        duration: 0.8 + Math.random() * 0.4
      }]);

      // Create splatter and ripple when drop "hits" the glass
      setTimeout(() => {
        // Remove the drop
        setActiveDrops(prev => prev.filter(d => d.id !== id));

        // Add splatter effect
        setSplatters(prev => [...prev.slice(-15), {
          id: Date.now() + Math.random(),
          x,
          y,
          size: 20 + Math.random() * 30
        }]);

        // Add ripple effect
        if (puddleVisible) {
          setRipples(prev => [...prev.slice(-10), {
            id: Date.now() + Math.random(),
            x,
            y,
            size: 30 + Math.random() * 40
          }]);
        }
      }, (0.8 + Math.random() * 0.4) * 1000);
    };

    // Spawn drops at intervals
    const interval = setInterval(spawnDrop, 150);

    return () => clearInterval(interval);
  }, [puddleVisible]);

  // Show puddle after initial fade-in
  useEffect(() => {
    const puddleTimer = setTimeout(() => {
      setPuddleVisible(true);
    }, 2000);

    // Ready to reveal after rain and ripples have formed
    const readyTimer = setTimeout(() => {
      setReadyToReveal(true);
    }, 5000);

    return () => {
      clearTimeout(puddleTimer);
      clearTimeout(readyTimer);
    };
  }, []);

  // Listen for key press to trigger poem reveal
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ignore if already triggered or not ready
      if (userTriggered || !readyToReveal) return;
      // Ignore modifier keys alone
      if (['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) return;
      setUserTriggered(true);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [userTriggered, readyToReveal]);

  // Gradual poem reveal - only after user presses a key
  useEffect(() => {
    if (!userTriggered) return;
    if (lines.length === 0) return;

    const totalDuration = 8000;
    const lineInterval = totalDuration / lines.length;

    let currentLine = 0;

    const revealTimer = setInterval(() => {
      currentLine++;
      setRevealProgress(currentLine);
      if (currentLine >= lines.length) {
        clearInterval(revealTimer);
      }
    }, lineInterval);

    return () => clearInterval(revealTimer);
  }, [userTriggered, lines.length]);

  // Auto-cycling audio with crossfade
  useEffect(() => {
    const SOUND_DURATION = 35000;
    const FADE_DURATION = 2000;

    const crossfade = (fadeOutRef, fadeInRef, newSrc) => {
      if (!fadeOutRef.current || !fadeInRef.current) return;

      fadeInRef.current.src = newSrc;
      fadeInRef.current.volume = 0;
      fadeInRef.current.play().catch(() => {});

      const steps = 20;
      const stepDuration = FADE_DURATION / steps;
      let step = 0;

      const fadeInterval = setInterval(() => {
        step++;
        const progress = step / steps;

        if (fadeInRef.current) {
          fadeInRef.current.volume = Math.min(0.25, progress * 0.25);
        }
        if (fadeOutRef.current) {
          fadeOutRef.current.volume = Math.max(0, 0.25 * (1 - progress));
        }

        if (step >= steps) {
          clearInterval(fadeInterval);
          if (fadeOutRef.current) {
            fadeOutRef.current.pause();
          }
        }
      }, stepDuration);
    };

    const initAudio = () => {
      if (audioRef1.current) {
        audioRef1.current.src = soundSources[0];
        audioRef1.current.volume = 0.25;
        audioRef1.current.play().catch(() => {});
      }
    };

    const initTimer = setTimeout(initAudio, 1000);

    const cycleTimer = setInterval(() => {
      soundIndexRef.current = (soundIndexRef.current + 1) % soundSources.length;
      const newSrc = soundSources[soundIndexRef.current];

      if (activeAudioRef.current === 1) {
        crossfade(audioRef1, audioRef2, newSrc);
        activeAudioRef.current = 2;
      } else {
        crossfade(audioRef2, audioRef1, newSrc);
        activeAudioRef.current = 1;
      }
    }, SOUND_DURATION);

    return () => {
      clearTimeout(initTimer);
      clearInterval(cycleTimer);
      if (audioRef1.current) {
        audioRef1.current.pause();
      }
      if (audioRef2.current) {
        audioRef2.current.pause();
      }
    };
  }, [soundSources]);

  return (
    <motion.div
      className="rain-view"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
    >
      {/* Sky background - looking up at clouds */}
      <div className="rain-background" />

      {/* Cloud layer for depth */}
      <motion.div
        className="clouds-layer"
        animate={{
          opacity: [0.25, 0.35, 0.25],
          scale: [1, 1.02, 1]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Raindrops falling toward viewer */}
      <div className="rain-container">
        <AnimatePresence>
          {activeDrops.map(drop => (
            <motion.div
              key={drop.id}
              className="raindrop"
              style={{
                left: `${drop.x}%`,
                top: `${drop.y}%`
              }}
              initial={{
                scale: 0.3,
                opacity: 0
              }}
              animate={{
                scale: [0.3, 2.5],
                opacity: [0, 0.9, 0.7, 0]
              }}
              transition={{
                duration: drop.duration,
                ease: "easeIn"
              }}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Splatter effects when drops hit */}
      <div className="rain-container">
        <AnimatePresence>
          {splatters.map(splat => (
            <motion.div
              key={splat.id}
              className="splatter"
              style={{
                left: `${splat.x}%`,
                top: `${splat.y}%`,
                width: splat.size,
                height: splat.size,
                marginLeft: -splat.size / 2,
                marginTop: -splat.size / 2
              }}
              initial={{ scale: 0.5, opacity: 0.8 }}
              animate={{ scale: 2, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Water streaks on glass */}
      {waterStreaks.map(streak => (
        <motion.div
          key={streak.id}
          className="water-streak"
          style={{
            left: `${streak.left}%`,
            top: `${streak.top}%`,
            height: streak.height
          }}
          initial={{ opacity: 0, scaleY: 0 }}
          animate={{
            opacity: [0, 0.4, 0.3, 0],
            scaleY: [0, 1, 1, 0.5],
            y: [0, 50, 100, 150]
          }}
          transition={{
            duration: streak.duration,
            delay: streak.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}

      {/* Glass puddle overlay */}
      <motion.div
        className="glass-puddle"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: puddleVisible ? 1 : 0,
          scale: puddleVisible ? 1 : 0.8
        }}
        transition={{ duration: 2.5, ease: "easeOut" }}
      />

      {/* Ripple effects on glass */}
      <div className="ripples-container">
        <AnimatePresence>
          {ripples.map(ripple => (
            <motion.div
              key={ripple.id}
              className="ripple"
              style={{
                left: `${ripple.x}%`,
                top: `${ripple.y}%`,
                width: ripple.size,
                height: ripple.size,
                marginLeft: -ripple.size / 2,
                marginTop: -ripple.size / 2
              }}
              initial={{ scale: 0, opacity: 0.6 }}
              animate={{ scale: 4, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2, ease: "easeOut" }}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Title */}
      <motion.h2
        className="rain-title"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 1.2 }}
      >
        {poem?.title || 'Untitled'}
      </motion.h2>

      {/* Prompt to press key - only show when ready and not yet triggered */}
      {readyToReveal && !userTriggered && (
        <motion.div
          className="rain-prompt"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.4, 0.7, 0.4] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          press any key to listen...
        </motion.div>
      )}

      {/* Poem content - words form from ripples */}
      {userTriggered && (
        <div className="rain-poem-container">
          {lines.map((line, index) => (
            <motion.p
              key={index}
              className="rain-line"
              initial={{
                opacity: 0,
                scale: 0.1,
                filter: 'blur(12px)',
                y: 20
              }}
              animate={{
                opacity: index < revealProgress ? 1 : 0,
                scale: index < revealProgress ? 1 : 0.1,
                filter: index < revealProgress ? 'blur(0px)' : 'blur(12px)',
                y: index < revealProgress ? 0 : 20
              }}
              transition={{
                duration: 2.5,
                ease: [0.25, 0.1, 0.25, 1],
                scale: {
                  type: "spring",
                  stiffness: 50,
                  damping: 15
                }
              }}
            >
              {/* Ripple ring behind each line as it forms */}
              {index < revealProgress && (
                <motion.span
                  className="line-ripple"
                  initial={{ scale: 0.5, opacity: 0.8 }}
                  animate={{ scale: 3, opacity: 0 }}
                  transition={{ duration: 2, ease: "easeOut" }}
                />
              )}
              {line || '\u00A0'}
            </motion.p>
          ))}
        </div>
      )}

      {/* Author signature - only after poem fully revealed */}
      {userTriggered && (
        <motion.div
          className="rain-author"
          initial={{ opacity: 0 }}
          animate={{ opacity: revealProgress >= lines.length ? 1 : 0 }}
          transition={{ duration: 1.5, delay: 0.5 }}
        >
          — {poem?.author || 'Anonymous'}
        </motion.div>
      )}

      {/* Audio elements for crossfade */}
      <audio ref={audioRef1} loop preload="auto" />
      <audio ref={audioRef2} loop preload="auto" />

      {/* Back button */}
      <motion.button
        className="rain-back-button"
        onClick={onBack}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        whileHover={{
          scale: 1.02,
          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4), 0 0 20px rgba(100, 148, 237, 0.4)"
        }}
        whileTap={{ scale: 0.98 }}
      >
        ← Return to Garden
      </motion.button>
    </motion.div>
  );
};

export default RainView;
